import React from "react";
import { FileText } from "lucide-react";
import { Btn, Kv } from "@/components/portal/crm/kit";
import { useLightbox } from "@/components/portal/FileLightbox";
import { day } from "@/lib/companyCheck";
import { Flags, PdfButton } from "./CompanyCheckCard";
import { Accounts, Ccjs, Credit, DocFigures, Filing, Owners, People, Section } from "./parts";
import "@/styles/company-check.css";

/** The full company check — every figure with the date it's from. */
export function CompanyCheckReport({ cc, testId = "company-report" }) {
  const { openOrDownload, lightbox } = useLightbox();
  const d = cc.data;
  const r = d?.report;
  if (!r) return <p className="sec-sub" data-testid={`${testId}-empty`}>{d?.reason || "No company check on this case."}</p>;
  const c = r.company || {};
  return (
    <div className="space-y-5" data-testid={testId}>
      <Section title="What stands out" sub={`Connexis Company Check, pulled ${day(d.checked_at)}.`} testId={`${testId}-findings`}
               right={<PdfButton cc={cc} testId={`${testId}-pdf`} />}>
        <Flags flags={[...(d.highlights || []), ...(r.flags || [])]} testId={`${testId}-flags`} />
        {r.accounts_file && (
          <div style={{ marginTop: 14 }}>
            <Btn kind="ghost" sm onClick={() => openOrDownload({ id: r.accounts_file.id, filename: r.accounts_file.filename, content_type: "application/pdf" })}
                 testId={`${testId}-accounts-pdf`}>
              <FileText className="size-3.5" /> {r.accounts_file.filename}
            </Btn>
          </div>
        )}
      </Section>
      <div className="cc-grid">
        {"credit" in r && (
          <Section title="Credit score" sub="Up to 3 years" testId={`${testId}-credit`}><Credit rows={r.credit} testId={`${testId}-credit-table`} /></Section>
        )}
        <Section title="County court judgments" sub="Last 6 years" testId={`${testId}-ccj`}><Ccjs ccj={r.ccj} testId={`${testId}-ccj-list`} /></Section>
      </div>
      <Section title="Filed accounts" sub="Up to 3 years, as filed at Companies House" testId={`${testId}-accounts`}>
        <Accounts rows={r.financials} filing={r.filing} testId={`${testId}-accounts-table`} />
      </Section>
      <Section title="From the customer's documents" sub="Read by Connie" testId={`${testId}-docs`}>
        <DocFigures docs={d.docs} testId={`${testId}-docs-list`} />
      </Section>
      <div className="cc-grid">
        <Section title="Directors" sub="Active, plus anyone who left in the last 12 months" testId={`${testId}-people`}>
          <People directors={r.directors} resigned={r.resigned_recently} testId={`${testId}-people-list`} />
        </Section>
        <Section title="Who owns it" sub="Shareholders and group" testId={`${testId}-owners`}>
          <Owners sh={r.shareholders} group={r.group} testId={`${testId}-owners-list`} />
        </Section>
      </div>
      <div className="cc-grid">
        <Section title="Filing" testId={`${testId}-filing`}><Filing r={r} testId={`${testId}-filing-list`} /></Section>
        <Section title="Company details" testId={`${testId}-details`}>
          <Kv rows={[
            ["Registered office", c.address || "—"],
            ["Incorporated", day(c.incorporated)],
            ["Type", [c.type, c.size].filter(Boolean).join(" · ") || "—"],
            ["Nature of business", (c.sic || []).map((s) => `${s.code} ${s.description || ""}`.trim()).join("; ") || "—"],
            ["VAT number", c.vat_number || "—"],
            c.previous_names?.length ? ["Previous names", c.previous_names.map((p) => `${p.name} (until ${day(p.until)})`).join("; ")] : null,
          ]} />
        </Section>
      </div>
      {lightbox}
    </div>
  );
}

export default CompanyCheckReport;
